import React, { useCallback, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import {
  ativar2fa,
  buscarStatus2fa,
  desativar2fa,
  iniciar2fa,
  regenerarCodigos2fa,
} from '../../../../auth/core/_requests'
import { Fator2faStatus, MfaSetup } from '../../../../auth/core/_models'
import { totpSchema } from '../../../../auth/components/validation/mfaSchema'
import { RecoveryCodes } from '../../../../auth/components/RecoveryCodes'
import { MfaSetupInstructions } from '../../../../auth/components/MfaSetupInstructions'
import { Button, FormCodeInput, LoadingIndicator } from '../../../../../components'
import {
  ButtonType,
  showAlert,
  showError,
  showFillCodigo2fa,
} from '../../../../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'

export const TwoFactorAuth: React.FC = () => {
  const { t } = useT()
  const [status, setStatus] = useState<Fator2faStatus>()
  const [setup, setSetup] = useState<MfaSetup>()
  const [codigos, setCodigos] = useState<string[]>()
  const [processando, setProcessando] = useState(false)

  const carregarStatus = useCallback(async () => {
    try {
      setStatus(await buscarStatus2fa())
    } catch (error) {
      showError(error)
    }
  }, [])

  useEffect(() => {
    carregarStatus()
  }, [carregarStatus])

  const formik = useFormik({
    initialValues: { codigo: '' },
    validationSchema: totpSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true)
      try {
        const resposta = await ativar2fa(values.codigo)
        setSetup(undefined)
        resetForm()
        setCodigos(resposta.codigosRecuperacao)
        await carregarStatus()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const iniciar = async () => {
    setProcessando(true)
    try {
      setSetup(await iniciar2fa())
    } catch (error) {
      showError(error)
    } finally {
      setProcessando(false)
    }
  }

  const desativar = async () => {
    const codigo = await showFillCodigo2fa({
      title: 'Desativar a verificação em duas etapas?',
      text: 'Sua conta passará a depender apenas da senha. Informe o código do autenticador para confirmar.',
    })
    if (!codigo) return

    setProcessando(true)
    try {
      await desativar2fa(codigo)
      await carregarStatus()
      showAlert({
        title: t('common.success'),
        text: 'A verificação em duas etapas foi desativada.',
        icon: 'success',
      })
    } catch (error) {
      showError(error)
    } finally {
      setProcessando(false)
    }
  }

  const regenerar = async () => {
    const codigo = await showFillCodigo2fa({
      title: 'Gerar novos códigos de recuperação?',
      text: 'Os códigos anteriores deixarão de funcionar. Informe o código do autenticador para confirmar.',
    })
    if (!codigo) return

    setProcessando(true)
    try {
      setCodigos((await regenerarCodigos2fa(codigo)).codigosRecuperacao)
      await carregarStatus()
    } catch (error) {
      showError(error)
    } finally {
      setProcessando(false)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_account_two_factor'
        aria-expanded='true'
        aria-controls='kt_account_two_factor'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Verificação em duas etapas</h3>
        </div>
      </div>

      <div id='kt_account_two_factor' className='collapse show'>
        <div className='card-body border-top p-9'>
          {!status && <LoadingIndicator />}

          {status && codigos && (
            <>
              <RecoveryCodes codigos={codigos} />
              <Button onClick={() => setCodigos(undefined)} className='btn-primary mt-5'>
                Fechar
              </Button>
            </>
          )}

          {status && !codigos && setup && (
            <form onSubmit={formik.handleSubmit} noValidate autoComplete='off'>
              <MfaSetupInstructions setup={setup} layout='lado-a-lado' className='mb-5' />

              <div className='row'>
                <div className='col-lg-5'>
                  <FormCodeInput
                    formik={formik}
                    name='codigo'
                    label='Código de verificação'
                    className='mb-5'
                    isRequired
                  />
                </div>
              </div>

              <div className='d-flex flex-wrap gap-3'>
                <Button
                  type={ButtonType.Submit}
                  className='btn-primary'
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? <LoadingIndicator /> : 'Ativar'}
                </Button>
                <Button
                  onClick={() => setSetup(undefined)}
                  className='btn-secondary'
                  disabled={formik.isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          )}

          {status && !codigos && !setup && (
            <>
              <div className='d-flex flex-wrap align-items-center mb-5 gap-3'>
                <span className={`badge ${status.ativo ? 'badge-light-success' : 'badge-light'}`}>
                  {status.ativo ? 'Ativa' : 'Inativa'}
                </span>
                {status.exigido && (
                  <span className='badge badge-light-primary'>Exigida pela sua instituição</span>
                )}
              </div>

              <div className='fw-bold text-gray-600 mb-8'>
                {status.ativo
                  ? `Você tem ${status.codigosDisponiveis} código(s) de recuperação disponível(is).`
                  : 'Um código do seu celular é pedido além da senha, o que impede o acesso mesmo que sua senha vaze.'}
                {status.ativo && !status.podeDesativar && (
                  <div className='mt-2'>
                    Sua empresa exige este controle, por isso ele não pode ser desativado.
                  </div>
                )}
              </div>

              <div className='d-flex flex-wrap gap-3'>
                {!status.ativo && (
                  <Button onClick={iniciar} className='btn-primary' disabled={processando}>
                    {processando ? <LoadingIndicator /> : 'Ativar verificação em duas etapas'}
                  </Button>
                )}
                {status.ativo && (
                  <Button onClick={regenerar} className='btn-light-primary' disabled={processando}>
                    Gerar novos códigos de recuperação
                  </Button>
                )}
                {status.ativo && status.podeDesativar && (
                  <Button onClick={desativar} className='btn-light-danger' disabled={processando}>
                    Desativar
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
